import type { DeliverableTableHeaderInterface, MilestoneTableHeaderInterface, TaskTableHeaderInterface } from '../../types/Tdm';

export const cclfProjectStatus: { [key: string]: string } = {
  REBUILD_INVENTORY: 'Rebuild Inventory',
  REBUILD_RFP: 'Rebuild RFP',
  DEVELOPER_AWARDED: 'Developer Awarded',
  DESIGN_BUILD: 'Design Build',
  FOR_SALE: 'For Sale',
  SOLD: 'Sold',
};

export const projectType = [
  'Affordable Homeownership',
  'Affordable Multi-Family',
  'Civic Facility',
  'Duplex',
  'Hospitality',
  'Library',
  'Market Multi-Family',
  'Medical Office',
  'Market-Rate Homeownership',
  'Mixed-Use',
  'Office',
  'Recreation Center',
  'Restaurant',
  'Retail',
  'School',
  'Single Family',
  'Townhome',
  'Workforce Housing',
  'Other',
].map((value) => ({ label: value, value }));

export const constructionType = ['Gut Rehabilitation', 'Historic Renovation', 'Interior Fitout', 'New Construction', 'Renovation'].map(
  (value) => ({ label: value, value })
);

export const tdmStatus: { [key: string]: string } = {
  NOT_STARTED: 'Not Started',
  TWENTY_FIVE: '25%',
  FIFTY: '50%',
  SEVENTY_FIVE: '75%',
  UNDER_REVIEW: 'Under Review',
  UNDER_REVISION: 'Under Revision',
  COMPLETE: 'Complete',
};

export const taskStatus: { [key: string]: string } = {
  NOT_STARTED: 'Not Started',
  TWENTY_FIVE: '25%',
  FIFTY: '50%',
  SEVENTY_FIVE: '75%',
  COMPLETE: 'Complete',
};
export const deliverableStatus: { [key: string]: string } = {
  NOT_STARTED: 'Not Started',
  TWENTY_FIVE: '25%',
  FIFTY: '50%',
  SEVENTY_FIVE: '75%',
  UNDER_REVIEW: 'Under Review',
  UNDER_REVISION: 'Under Revision',
  COMPLETE: 'Complete',
};
export const milestoneStatus: { [key: string]: string } = {
  NOT_STARTED: 'Not Started',
  TWENTY_FIVE: '25%',
  FIFTY: '50%',
  SEVENTY_FIVE: '75%',
  UNDER_REVIEW: 'Under Review',
  UNDER_REVISION: 'Under Revision',
  COMPLETE: 'Complete',
};

export const taskCategories: {
  [key: string]: { name: string; code: string }[];
} = {
  acquisition: [
    { name: 'Closing', code: 'CLOS' },
    { name: 'Contract and Legal Review', code: 'CALR' },
    { name: 'Property Due Diligence', code: 'PDUD' },
  ],
  finance: [
    { name: 'Communication', code: 'COMM' },
    { name: 'Debt Capitalization', code: 'DTCP' },
    { name: 'Sensitivity Analysis', code: 'SNAN' },
    { name: 'Underwriting', code: 'UNWR' },
  ],
  procurement: [
    { name: 'Developer Selection', code: 'DVSL' },
    { name: 'Vendor Management: Architect', code: 'VMNA' },
    { name: 'Vendor Management: Civil Engineer', code: 'VMCE' },
    { name: 'Vendor Management: General Contractor', code: 'VMGC' },
    { name: 'Vendor Management: Geotech Consultant', code: 'VMGT' },
    { name: 'Vendor Management: Structural Engineer', code: 'VMSE' },
  ],
  development: [
    { name: 'Administrative', code: 'ADMT' },
    { name: 'Community Engagement', code: 'CMEG' },
    { name: 'Compliance Due Diligence', code: 'CMDD' },
    { name: 'Developer Financing', code: 'DVFN' },
    { name: 'Developer Property Acquisition', code: 'DPAQ' },
    { name: 'Due Diligence Budget', code: 'DDLB' },
    { name: 'Partnership Documents', code: 'PRTD' },
    { name: 'Physical Due Diligence', code: 'PHDD' },
    { name: 'Property Disposition', code: 'PRDP' },
    { name: 'Property Development Plan and Budget', code: 'PDPB' },
    { name: 'Project Market Analysis', code: 'PMKA' },
  ],
  design: [
    { name: 'Design Iterating', code: 'DSIT' },
    { name: 'Design Team', code: 'DSTM' },
    { name: 'Permitting', code: 'PRMT' },
  ],
  construction: [
    { name: 'Construction Closeout', code: 'CNCL' },
    { name: 'During Construction', code: 'DUCN' },
    { name: 'Pre-Construction', code: 'PCSR' },
  ],
  shared: [
    { name: 'Specific to: New Construction', code: 'STNC' },
    { name: 'Specific to: Historic Renovation', code: 'STHR' },
    { name: 'Specific to: Renovation', code: 'STRN' },
    { name: 'Specific to: Adaptive Re-Use', code: 'SPAR' },
    { name: 'Specific to: Interior Fitout', code: 'UNKN' },
    { name: 'Specific to: Single Family', code: 'UNKN' },
    { name: 'Specific to: Duplexes', code: 'UNKN' },
    { name: 'Specific to: Small Multifamily', code: 'UNKN' },
    { name: 'Specific to: Large Multifamily', code: 'UNKN' },
    { name: 'Specific to: Hotel', code: 'UNKN' },
    { name: 'Specific to: Schools', code: 'UNKN' },
    { name: 'Specific to: Office', code: 'UNKN' },
    { name: 'Specific to: Restaurants', code: 'UNKN' },
  ],
};

export const tasksTableHeader: TaskTableHeaderInterface[] = [
  { name: 'Task', customClassName: 'w-72' },
  { name: 'Status', customClassName: 'w-36' },
  { name: 'Lead', customClassName: 'w-40' },
  { name: 'Support', customClassName: 'w-40' },
  { name: 'Vendor', customClassName: 'w-40' },
  { name: 'Due Date', customClassName: 'w-24' },
  { name: 'Actions', customClassName: 'w-24' },
];
export const deliverablesTableHeader: DeliverableTableHeaderInterface[] = [
  { name: 'Deliverable', customClassName: 'w-72' },
  { name: 'Status', customClassName: 'w-36' },
  { name: 'Lead', customClassName: 'w-40' },
  { name: 'Support', customClassName: 'w-40' },
  { name: 'Vendor', customClassName: 'w-40' },
  { name: 'Due Date', customClassName: 'w-24' },
  { name: 'Actions', customClassName: 'w-24' },
];
export const milestonesTableHeader: MilestoneTableHeaderInterface[] = [
  { name: 'Milestone', customClassName: 'w-72' },
  { name: 'Status', customClassName: 'w-36' },
  { name: 'Lead', customClassName: 'w-40' },
  { name: 'Support', customClassName: 'w-40' },
  { name: 'Vendor', customClassName: 'w-40' },
  { name: 'Due Date', customClassName: 'w-24' },
  { name: 'Actions', customClassName: 'w-24' },
];

export const neighborHoods = [
  { name: 'Albany Park', city: 'chicago' },
  { name: 'Archer Heights', city: 'chicago' },
  { name: 'Armour Square ', city: 'chicago' },
  { name: 'Ashburn', city: 'chicago' },
  { name: 'Auburn Gresham', city: 'chicago' },
  { name: 'Austin', city: 'chicago' },
  { name: 'Avalon Park', city: 'chicago' },
  { name: 'Avondale', city: 'chicago' },
  { name: 'Belmont Cragin', city: 'chicago' },
  { name: 'Beverly', city: 'chicago' },
  { name: 'Bridgeport', city: 'chicago' },
  { name: 'Brighton Park', city: 'chicago' },
  { name: 'Burnside', city: 'chicago' },
  { name: 'Calumet Heights', city: 'chicago' },
  { name: 'Chatham', city: 'chicago' },
  { name: 'Chicago Lawn', city: 'chicago' },
  { name: 'Clearing', city: 'chicago' },
  { name: 'Douglas', city: 'chicago' },
  { name: 'Dunning', city: 'chicago' },
  { name: 'East Garfield Park', city: 'chicago' },
  { name: 'East Side', city: 'chicago' },
  { name: 'Edgewater', city: 'chicago' },
  { name: 'Edison Park', city: 'chicago' },
  { name: 'Englewood', city: 'chicago' },
  { name: 'Forest Glen', city: 'chicago' },
  { name: 'Fuller Park', city: 'chicago' },
  { name: 'Gage Park', city: 'chicago' },
  { name: 'Garfield Ridge', city: 'chicago' },
  { name: 'Grand Boulevard', city: 'chicago' },
  { name: 'Greater Grand Crossing', city: 'chicago' },
  { name: 'Hegewisch', city: 'chicago' },
  { name: 'Hermosa', city: 'chicago' },
  { name: 'Humboldt Park', city: 'chicago' },
  { name: 'Hyde Park', city: 'chicago' },
  { name: 'Irving Park', city: 'chicago' },
  { name: 'Jefferson Park', city: 'chicago' },
  { name: 'Kenwood', city: 'chicago' },
  { name: 'Lakeview', city: 'chicago' },
  { name: 'Lincoln Park', city: 'chicago' },
  { name: 'Lincoln Square', city: 'chicago' },
  { name: 'Logan Square', city: 'chicago' },
  { name: 'Loop', city: 'chicago' },
  { name: 'Lower West Side', city: 'chicago' },
  { name: 'McKinley Park', city: 'chicago' },
  { name: 'Montclare', city: 'chicago' },
  { name: 'Morgan Park', city: 'chicago' },
  { name: 'Mount Greenwood', city: 'chicago' },
  { name: 'Near North Side', city: 'chicago' },
  { name: 'Near South Side', city: 'chicago' },
  { name: 'Near West Side', city: 'chicago' },
  { name: 'New City', city: 'chicago' },
  { name: 'North Center', city: 'chicago' },
  { name: 'North Lawndale', city: 'chicago' },
  { name: 'North Park', city: 'chicago' },
  { name: 'Norwood Park', city: 'chicago' },
  { name: 'O’Hare', city: 'chicago' },
  { name: 'Oakland', city: 'chicago' },
  { name: 'Portage Park', city: 'chicago' },
  { name: 'Pullman', city: 'chicago' },
  { name: 'Riverdale', city: 'chicago' },
  { name: 'Rogers Park', city: 'chicago' },
  { name: 'Roseland', city: 'chicago' },
  { name: 'South Chicago', city: 'chicago' },
  { name: 'South Deering', city: 'chicago' },
  { name: 'South Lawndale', city: 'chicago' },
  { name: 'South Shore', city: 'chicago' },
  { name: 'Uptown', city: 'chicago' },
  { name: 'Washington Heights', city: 'chicago' },
  { name: 'Washington Park', city: 'chicago' },
  { name: 'West Elsdon', city: 'chicago' },
  { name: 'West Englewood', city: 'chicago' },
  { name: 'West Garfield Park', city: 'chicago' },
  { name: 'West Lawn', city: 'chicago' },
  { name: 'West Pullman', city: 'chicago' },
  { name: 'West Ridge', city: 'chicago' },
  { name: 'West Town', city: 'chicago' },
  { name: 'Woodlawn', city: 'chicago' },
];

export const birminghamNeighborhoods = [
  'Acipco-Finley',
  'Airport Highlands',
  'Apple Valley',
  'Arlington-West End',
  'Belview Heights',
  'Bridlewood',
  'Brown Springs',
  'Brownsville Heights',
  'Brummitt Heights',
  'Bush Hills',
  'Central City',
  'Central Park',
  'Central Pratt',
  'College Hills',
  'Collegeville',
  'Crestline',
  'Crestwood North',
  'Crestwood South',
  'Dolomite',
  'Druid Hills',
  'East Avondale',
  'East Birmingham',
  'East Brownville',
  'East Lake ',
  'East Thomas',
  'Eastwood',
  'Echo Highlands',
  'Enon Ridge',
  'Ensley',
  'Ensley Highlands',
  'Evergreen ',
  'Fairmont',
  'Fairview',
  'Five Points South',
  'Forest Park-South Avondale',
  'Fountain Heights',
  'Garden Highlands',
  'Gate City',
  'Germania Park',
  'Glen Iris',
  'Grasselli Heights',
  'Graymont',
  'Green Acres',
  'Harriman Park',
  'Highland Park',
  'Hillman',
  'Hillman Park',
  'Hooper City',
  'Huffman',
  'Industrial Center',
  'Inglenook',
  'Jones Valley',
  'Killough Springs',
  'Kingston',
  'Liberty Highlands',
  'Maple Grove',
  'Mason City',
  'North Avondale',
  'North Birmingham',
  'North East Lake',
  'North Pratt',
  'North Titusville',
  'Norwood',
  'Oak Ridge',
  'Oak Ridge Park',
  'Oakwood Place',
  'Overton',
  'Oxmoor',
  'Penfield Park',
  'Pine Knoll Vista',
  'Powderly',
  'Redmont Park',
  'Riley-Travellick',
  'Rising-West Princeton',
  'Roebuck',
  ' Roebuck Springs-South Roebuck',
  'Roosevelt',
  'Sandusky',
  'Sherman Heights',
  'Smithfield',
  'Smithfield Estates',
  'South East Lake',
  'South Pratt',
  'Southside',
  'South Titusville',
  'South Woodlawn',
  'Spring Lake',
  'Sun Valley',
  'Tarpley City',
  'Thomas',
  'Tuxedo',
  'Wahouma',
  'West Brownville',
  'West End Manor',
  'West Goldwire',
  'Woodland Park',
  'Woodlawn',
  'Wylam',
  'Zion City',
];

export const birminghamDistricts = {
  1: [
    'Archer Heights',
    'Apple Valley',
    'Bridlewood',
    'Echo Highlands',
    'Huffman',
    'Killough Springs',
    'Roebuck',
    'Spring Lake',
    'Sun Valley',
  ],
  2: ['Brown Springs', 'Crestline', 'Crestwood', 'South', 'Eastwood', 'Liberty Highland', 'Overton', 'Roebuck Springs', 'South East Lake'],
  3: ['Crestwood South', 'East Avondale', 'Five Points South', 'Forest Park/South Avondale', 'Glen Iris', 'Highland Park', 'Redmont Park'],
  4: [
    'Airport Highland',
    'Brownsville Heights',
    'Brummitt Heights',
    'Collegeville',
    'East Birmingham',
    'Fairmont',
    'Harriman Park',
    'Inglenook',
    'Killough Springs',
    'Kingston',
    'Maple Grove',
    'North East Lake',
    'Norwood',
    'Penfield Parks',
    'Pine Knoll Vista',
    'Roebuck',
    'South Woodlawn',
    'Woodlawn',
    'Zion City',
  ],
  5: ['Oakwood Place', 'South Titusville', 'West End Manor', 'Woodland Park'],
  6: [
    'Hillman',
    'Hillman Park',
    'Industrial Center',
    'Jones Valley',
    'Oxmoor Valley',
    'Powderly',
    'Riley Travellick',
    'Roosevelt City',
    'Tarpley City',
    'West Brownville',
    'West Goldwire',
  ],
  7: ['Ensley Highlands', 'Fairview', 'Graymont', 'Thomas', 'Rising West-Princeton', 'Tuxedo', 'Tuxedo Heights'],
  8: [
    'Enon Ridge',
    'Ensley',
    'Evergreen',
    'Fountain Heights',
    'Hooper City',
    'North Birmingham',
    'North Pratt',
    'Oak Ridge',
    'Sandusky',
    'Sherman Heights',
    'Smithfield Estates',
    'South Pratt',
    'Wylam',
  ],
};

export const usBoundaries = [
  {
    state: {
      long_name: 'Alabama',
      short_name: 'AL',
    },
    cities: [
      {
        name: 'Birmingham',
        district: [
          {
            number: 1,
            neighborhoods: [
              'Apple Valley',
              'Bridlewood',
              'Echo Highlands',
              'Huffman',
              'Killough Springs',
              'Roebuck',
              'Spring Lake',
              'Sun Valley',
            ],
          },
          {
            number: 2,
            neighborhoods: [
              'Brown Springs',
              'Crestline',
              'Crestwood',
              'South',
              'Eastwood',
              'Liberty Highland',
              'Overton',
              'Roebuck Springs',
              'South East Lake',
            ],
          },
          {
            number: 3,
            neighborhoods: [
              'Crestwood South',
              'East Avondale',
              'Five Points South',
              'Forest Park/South Avondale',
              'Glen Iris',
              'Highland Park',
              'Redmont Park',
            ],
          },
          {
            number: 4,
            neighborhoods: [
              'Airport Highland',
              'Brownsville Heights',
              'Brummitt Heights',
              'Collegeville',
              'East Birmingham',
              'Fairmont',
              'Harriman Park',
              'Inglenook',
              'Killough Springs',
              'Kingston',
              'Maple Grove',
              'North East Lake',
              'Norwood',
              'Penfield Parks',
              'Pine Knoll Vista',
              'Roebuck',
              'South Woodlawn',
              'Woodlawn',
              'Zion City',
            ],
          },
          {
            number: 5,
            neighborhoods: [
              'Central City',
              'Crestwood North',
              'East Avondale',
              'Eastlake',
              'Eastwood',
              'Five Points South',
              'Forest Park/South Avondale',
              'Fountain Heights',
              'Gate City',
              'North Avondale',
              'Oak Ridge Park',
              'Smithfield',
              'Southside',
              'South Eastlake',
              'Wahouma',
            ],
          },
          {
            number: 6,
            neighborhoods: [
              'Arlington West End',
              'Five Points South',
              'Glen Iris',
              'Mason City',
              'North Titusville',
              'Oakwood Place',
              'South Titusville',
              'West End Manor',
              'Woodland Park',
            ],
          },
          {
            number: 7,
            neighborhoods: [
              'East Brownville',
              'Garden Highlands',
              'Germania Park',
              'Grasselli Heights',
              'Green Acres',
              'Hillman',
              'Hillman Park',
              'Industrial Center',
              'Jones Valley',
              'Oxmoor Valley',
              'Powderly',
              'Riley Travellick',
              'Roosevelt City',
              'Tarpley City',
              'West Brownville',
              'West Goldwire',
            ],
          },
          {
            number: 8,
            neighborhoods: [
              'Belview Heights',
              'Bush Hills',
              'Central Park',
              'College Hills',
              'Ensley',
              'Ensley Highlands',
              'Fairview',
              'Graymont',
              'Thomas',
              'Rising West-Princeton',
              'Tuxedo',
              'Tuxedo Heights',
            ],
          },
          {
            number: 9,
            neighborhoods: [
              'Acipo-Finley',
              'Central Pratt',
              'Dolomite',
              'Druid Hill',
              'East Thomas',
              'Enon Ridge',
              'Ensley',
              'Evergreen',
              'Fountain Heights',
              'Hooper City',
              'North Birmingham',
              'North Pratt',
              'Oak Ridge',
              'Sandusky',
              'Sherman Heights',
              'Smithfield Estates',
              'South Pratt',
              'Wylam',
            ],
          },
        ],
      },
    ],
  },
];
